import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Check, ChevronDown, ClipboardList, Plus, X } from 'lucide-react';
import { toast } from 'react-toastify';

import type { IConsultation, IJournalResult } from '../../store/types';
import { getTime } from '../../utils/getTime';
import { AiAbstractTab } from '../../pages/journal/ai-abstract-tab';
import { AiCoachSupervisorTab } from '../../pages/journal/ai-coach-supervisor-tab';
import { AiHomeworkTab } from '../../pages/journal/ai-homework-tab';
import { AiMetaphorTab } from '../../pages/journal/ai-metaphor-tab';
import { AiSupervisorTab } from '../../pages/journal/ai-supervisor-tab';
import { AiTalesTherapistTab } from '../../pages/journal/ai-tales-therapist-tab';
import { AiTranscriptionTab } from '../../pages/journal/ai-transcription-tab';
import { AddReportTab } from '../../pages/journal/add-report-tab';
import { JournalMainTab } from '../../pages/journal/journal-main-tab';
import { JournalModalPanel } from '../../pages/journal/journal-modal-panel';
import { buildJournalResultSaveBody, getEmptyJournalResult } from '../../pages/journal/journal-result-save';
import type { Comments } from '../../pages/journal/types';
import { useEditConsultationMutation, useGetConsultationQuery } from '../../services/users.api';
import ModalWrap from '../common/modal-wrap';

export interface SessionDetailSession {
  id: number;
  date: string;
  rawDate: string;
  datetimeStart: string;
  datetimeEnd: string;
  meetData: IJournalResult | null;
  title: string;
  summary: string | null;
  orderNumber?: number;
}

const truncate = (text: string, maxLen: number) => {
  const trimmed = text.trim();
  if (trimmed.length <= maxLen) return trimmed;
  return `${trimmed.slice(0, maxLen)}…`;
};

/** Карточка сессии для шапки SessionDetail из данных консультации. */
export function buildSessionDetailSession(consultation: IConsultation): SessionDetailSession {
  const first = consultation.result?.[0];
  const fromConclusion = first?.result?.trim();
  const fromDpdg = first?.dpdg?.comment?.trim();
  const fromQuestion = first?.questions?.[0]?.question?.trim();
  let title = 'Без названия';
  if (fromConclusion) title = truncate(fromConclusion.split('\n')[0] ?? fromConclusion, 70);
  else if (fromDpdg) title = truncate(fromDpdg.split('\n')[0] ?? fromDpdg, 70);
  else if (fromQuestion) title = truncate(fromQuestion.split('\n')[0] ?? fromQuestion, 70);

  const summaryCandidate = fromDpdg || fromConclusion;
  const summary =
    summaryCandidate && summaryCandidate.length > 0
      ? truncate(summaryCandidate.split('\n')[0] ?? summaryCandidate, 160)
      : null;

  const date = `${dayjs(new Date(consultation.date)).format('DD.MM.YYYY')} ${getTime(new Date(consultation.datetime_start))}`;

  return {
    id: consultation.id,
    date,
    rawDate: consultation.date,
    datetimeStart: consultation.datetime_start,
    datetimeEnd: consultation.datetime_end,
    meetData: consultation.result?.[0] ?? null,
    title: title === 'Без названия' ? 'Сессия' : title,
    summary,
    orderNumber: consultation.consultation_number,
  };
}

export interface SessionDetailProps {
  /** Для запросов /consultation-service/.../results/* */
  consultationId: number;
  session: SessionDetailSession;
  onBack: () => void;
  /** Подпись вкладки из `reportTypes`, например «ИИ супервизия» */
  initialTab?: string;
  clientName?: string;
}

const reportTypes = [
  'Инструменты',
  'Транскрипция',
  'ИИ супервизия',
  'Конспект',
  'Домашнее задание',
  'Сказкотерапия',
  'Метафоры',
  'ИИ ICF коуч',
  'SOAP',
  'DAP',
  'BIRP',
  'GIRP',
] as const;

type ReportType = (typeof reportTypes)[number];
type SessionDetailTab = ReportType | 'add';

const makeUrl = (url: string) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `${import.meta.env.VITE_REACT_APP_API_URL}/${url}`;
};

const getCommentsFromMeetData = (meetData: IJournalResult | null): Comments => ({
  emotions: meetData?.emotions?.map((el) => el.comment) || [],
  mac_cards: meetData?.mac_cards?.map((el) => el.comment) || [],
  wheels: meetData?.wheel?.map((el) => el.comment) || [],
  color_code: meetData?.color_code?.map((el) => el.comment) || [],
  dpdg: meetData?.dpdg ? meetData.dpdg.comment : '',
  clouds: meetData?.clouds?.map((el) => el.comment) || [],
  sand_therapy: meetData?.sand_therapy?.map((el) => el.comment) || [],
  result: meetData?.result || '',
  mac_cards_by_participant: meetData?.mac_cards_by_participant
    ? Object.fromEntries(
        Object.entries(meetData.mac_cards_by_participant).map(([id, entries]) => [id, entries.map((e) => e.comment ?? '')]),
      )
    : {},
  emotions_by_participant: meetData?.emotions_by_participant
    ? Object.fromEntries(
        Object.entries(meetData.emotions_by_participant).map(([id, entries]) => [id, entries.map((e) => e.comment ?? '')]),
      )
    : {},
  session_conclusion_by_participant: meetData?.session_conclusion_by_participant ?? {},
});

function ReportContent({
  activeReport,
  consultationId,
  meetData,
  commentsData,
  setCommentsData,
  isGroupConsultation,
  editMode,
  onSave,
  onToggleEditMode,
  onOpenConclusionTips,
}: {
  activeReport: SessionDetailTab;
  consultationId: number;
  meetData: IJournalResult | null;
  commentsData: Comments;
  setCommentsData: React.Dispatch<React.SetStateAction<Comments>>;
  isGroupConsultation: boolean;
  editMode: boolean;
  onSave: () => void;
  onToggleEditMode: () => void;
  onOpenConclusionTips: () => void;
}) {
  switch (activeReport) {
    case 'Транскрипция':
      return <AiTranscriptionTab consultationId={consultationId} bypassFeatureFlag />;
    case 'Инструменты':
      return (
        <JournalMainTab
          meetData={meetData}
          editMode={editMode}
          commentsData={commentsData}
          setCommentsData={setCommentsData}
          onSave={onSave}
          onToggleEditMode={onToggleEditMode}
          onOpenConclusionTips={onOpenConclusionTips}
          makeUrl={makeUrl}
          consultationId={consultationId}
          isGroupTherapy={isGroupConsultation}
        />
      );
    case 'ИИ супервизия':
      return <AiSupervisorTab consultationId={consultationId} />;
    case 'Конспект':
      return <AiAbstractTab consultationId={consultationId} />;
    case 'Домашнее задание':
      return <AiHomeworkTab consultationId={consultationId} />;
    case 'Сказкотерапия':
      return <AiTalesTherapistTab consultationId={consultationId} />;
    case 'Метафоры':
      return <AiMetaphorTab consultationId={consultationId} />;
    case 'ИИ ICF коуч':
      return <AiCoachSupervisorTab consultationId={consultationId} />;
    case 'SOAP':
    case 'DAP':
    case 'BIRP':
    case 'GIRP':
      return (
        <div className="py-8 text-center">
          <p className="text-blackBase font-semibold text-lg">Формат «{activeReport}» скоро будет доступен.</p>
        </div>
      );
    case 'add':
      return <AddReportTab />;
    default:
      return null;
  }
}

function normalizeInitialTab(tab: string | undefined): SessionDetailTab {
  if (!tab) return 'Инструменты';
  if (tab === 'add') return 'add';
  if (tab === 'Транскрипт') return 'Транскрипция';
  const found = reportTypes.find((t) => t === tab);
  return found ?? 'Инструменты';
}

export function SessionDetail({ consultationId, session, onBack, initialTab, clientName }: SessionDetailProps) {
  const { data: consultation } = useGetConsultationQuery(String(consultationId), { skip: !consultationId });
  const [editConsultation] = useEditConsultationMutation();
  const resolvedSession = useMemo(
    () => (consultation ? buildSessionDetailSession(consultation) : session),
    [consultation, session],
  );
  const resolvedMeetData = consultation?.result?.[0] ?? session.meetData;
  const baseMeetData = resolvedMeetData ?? getEmptyJournalResult();
  const resolvedClientName = clientName || consultation?.client_full_name || resolvedSession.title;
  const isGroupConsultation = Boolean(consultation?.is_group);
  const [activeReport, setActiveReport] = useState<SessionDetailTab>(() => normalizeInitialTab(initialTab));
  const [editMode, setEditMode] = useState(false);
  const [modalConclusion, setModalConclusion] = useState(false);
  const [isReportPickerOpen, setIsReportPickerOpen] = useState(false);
  const [commentsData, setCommentsData] = useState<Comments>(() => getCommentsFromMeetData(session.meetData));
  const reportPickerButtonRef = useRef<HTMLButtonElement>(null);
  const reportPickerDialogRef = useRef<HTMLDivElement>(null);

  const handleSave = useCallback(() => {
    if (!consultation) return;

    editConsultation({
      id: consultation.id,
      body: buildJournalResultSaveBody(baseMeetData, commentsData),
    })
      .unwrap()
      .then(() => {
        setEditMode(false);
        toast.success('Данные успешно сохранены');
      })
      .catch(() => {
        toast.error('Произошла ошибка');
      });
  }, [baseMeetData, commentsData, consultation, editConsultation]);

  const handleToggleEditMode = useCallback(() => {
    setEditMode((prev) => {
      const next = !prev;
      if (prev && !next) {
        setCommentsData(getCommentsFromMeetData(resolvedMeetData));
      }
      return next;
    });
  }, [resolvedMeetData]);

  const conclusionTips = useMemo(
    () => (
      <>
        <h2 className="font-comfortaa font-bold text-base lg:text-2xl mb-[20px]">Возможная структура заключения</h2>
        <ul className="list-decimal text-sm lg:text-lg text-blackBase">
          <li>{isGroupConsultation ? 'Тип встречи' : 'Тип клиента'}</li>
          <li>Наличие психиатрического диагноза</li>
          <li>Фармакотерапия (какие препараты)</li>
          <li>Жалоба \ проблема \ запрос</li>
          <li>Рабочая гипотеза</li>
          <li>{isGroupConsultation ? 'Протективные факторы встречи' : 'Протективные факторы Клиента'}</li>
          <li>{isGroupConsultation ? 'Установки встречи (если есть)' : 'Установки Клиента (если есть)'}</li>
          <li>Методы, которые использовались на сессии</li>
          <li>
            Техники и интервенции, которые использовались (например: сократический диалог, модуляция установок,
            дерефлексия, парадоксальная интенция, самораскрытие, отражение, конфронтация, прямое руководство, поддержка,
            информирование, библиотерапия, метод «Горячего стула» и т.п.)
          </li>
          <li>План дальнейшей работы</li>
        </ul>
      </>
    ),
    [isGroupConsultation],
  );
  const periodLabel = useMemo(() => {
    const start = new Date(resolvedSession.datetimeStart);
    const end = new Date(resolvedSession.datetimeEnd);
    const dateLabel = new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(start);
    return `${dateLabel}, ${getTime(start)} - ${getTime(end)}`;
  }, [resolvedSession.datetimeEnd, resolvedSession.datetimeStart]);

  useEffect(() => {
    setActiveReport(normalizeInitialTab(initialTab));
  }, [initialTab, consultationId]);

  useEffect(() => {
    setCommentsData(getCommentsFromMeetData(resolvedMeetData));
    setEditMode(false);
  }, [resolvedMeetData]);

  const closeReportPicker = useCallback(() => {
    setIsReportPickerOpen(false);
    window.requestAnimationFrame(() => reportPickerButtonRef.current?.focus());
  }, []);

  const selectReport = (report: SessionDetailTab) => {
    setActiveReport(report);
    closeReportPicker();
  };

  useEffect(() => {
    if (!isReportPickerOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeReportPicker();
        return;
      }

      if (event.key === 'Tab') {
        const focusable = Array.from(
          reportPickerDialogRef.current?.querySelectorAll<HTMLButtonElement>('button:not([disabled])') ?? [],
        );
        const first = focusable[0];
        const last = focusable.at(-1);
        if (!first || !last) return;

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.requestAnimationFrame(() => {
      reportPickerDialogRef.current
        ?.querySelector<HTMLButtonElement>('[aria-current="true"]')
        ?.focus();
    });

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeReportPicker, isReportPickerOpen]);

  return (
    <div className="min-w-0">
      <div className="mb-4 min-w-0">
        <button
          type="button"
          onClick={onBack}
          className="group mb-3 inline-flex min-h-11 touch-manipulation items-center gap-2 rounded-lg px-2 text-sm font-semibold text-blackBase transition-colors hover:bg-white hover:text-redBase focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-redBase md:mb-4"
          aria-label="Назад к сессиям"
        >
          <svg
            className="fill-blackBase group-hover:fill-redBase"
            width="21"
            height="16"
            viewBox="0 0 27 22"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M0.338423 11.8222C0.12172 11.604 1.21535e-07 11.3083 1.25211e-07 11C1.28888e-07 10.6917 0.12172 10.396 0.338423 10.1778L9.59592 0.870297C9.70186 0.755989 9.82961 0.664307 9.97156 0.600718C10.1135 0.537129 10.2667 0.502937 10.4221 0.500181C10.5775 0.497425 10.7318 0.526159 10.8759 0.584675C11.02 0.643191 11.1509 0.730286 11.2608 0.840763C11.3707 0.951243 11.4573 1.08284 11.5155 1.22771C11.5737 1.37258 11.6023 1.52775 11.5995 1.68397C11.5968 1.84018 11.5628 1.99424 11.4995 2.13696C11.4363 2.27967 11.3451 2.40812 11.2314 2.51463L3.94885 9.83656L25.8428 9.83656C26.1497 9.83656 26.4441 9.95913 26.6611 10.1773C26.8781 10.3955 27 10.6914 27 11C27 11.3086 26.8781 11.6045 26.6611 11.8227C26.4441 12.0409 26.1497 12.1634 25.8428 12.1634L3.94885 12.1634L11.2314 19.4854C11.3451 19.5919 11.4363 19.7203 11.4995 19.863C11.5628 20.0058 11.5968 20.1598 11.5995 20.316C11.6023 20.4722 11.5737 20.6274 11.5155 20.7723C11.4573 20.9172 11.3707 21.0488 11.2608 21.1592C11.1509 21.2697 11.02 21.3568 10.8759 21.4153C10.7318 21.4738 10.5775 21.5026 10.4221 21.4998C10.2667 21.4971 10.1135 21.4629 9.97156 21.3993C9.82961 21.3357 9.70186 21.244 9.59592 21.1297L0.338423 11.8222Z"
            />
          </svg>
          <span>Назад к сессиям</span>
        </button>
        <h1 className="text-pretty font-comfortaa text-2xl font-bold leading-tight text-blackBase md:hidden">
          {resolvedSession.orderNumber ? `Сессия №${resolvedSession.orderNumber}` : 'Сессия'}
        </h1>
        <h2 className="hidden font-comfortaa font-semibold text-3xl leading-none text-blackBase md:block md:text-6xl">
          {periodLabel}
        </h2>
      </div>

      <div className="mb-5 min-w-0 rounded-xl border border-gray-200 bg-white p-4 md:mb-6 md:border-0 md:bg-transparent md:p-0">
        <p className="mb-1 text-sm tabular-nums text-greyBase md:hidden">{periodLabel}</p>
        <span className="text-sm text-greyBase lg:text-lg">Клиент: </span>
        <span className="break-words text-sm font-medium text-blackBase lg:text-lg">{resolvedClientName}</span>
      </div>

      <button
        ref={reportPickerButtonRef}
        type="button"
        aria-expanded={isReportPickerOpen}
        aria-controls="journal-report-picker"
        onClick={() => setIsReportPickerOpen(true)}
        className="mb-4 flex min-h-12 w-full touch-manipulation items-center justify-between gap-3 rounded-xl border border-gray-300 bg-white px-4 py-3 text-left font-medium text-gray-900 shadow-sm transition-colors hover:border-redBase focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-redBase md:hidden"
      >
        <span className="min-w-0 truncate">Раздел: {activeReport === 'add' ? 'Добавить отчёт' : activeReport}</span>
        <ChevronDown size={20} className="shrink-0 text-gray-500" aria-hidden />
      </button>

      <div className="mb-4 hidden flex-wrap gap-2 border-b border-gray-200 pb-3 md:flex">
        {reportTypes.map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => setActiveReport(type)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-t-lg text-xs font-medium transition-colors border cursor-pointer ${
              activeReport === type
                ? 'border-[#4ECDC4] text-gray-900 bg-white border-b-white'
                : 'border-gray-200 text-gray-500 hover:bg-gray-50'
            }`}
          >
            <ClipboardList size={12} aria-hidden />
            {type}
          </button>
        ))}
          <button
            type="button"
            className={`flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg border cursor-pointer transition-colors ${
              activeReport === 'add'
                ? 'border-[#4ECDC4] text-gray-900 bg-white'
                : 'text-gray-500 border-gray-200 hover:bg-gray-50'
            }`}
            onClick={() => setActiveReport('add')}
          >
            <Plus size={12} aria-hidden />
            Добавить
          </button>
        </div>

      <div className="min-w-0 overflow-x-hidden rounded-xl border border-gray-200 bg-white p-4 md:p-6">
        <ReportContent
          activeReport={activeReport}
          consultationId={consultationId}
          meetData={resolvedMeetData}
          commentsData={commentsData}
          setCommentsData={setCommentsData}
          isGroupConsultation={isGroupConsultation}
          editMode={editMode}
          onSave={handleSave}
          onToggleEditMode={handleToggleEditMode}
          onOpenConclusionTips={() => setModalConclusion(true)}
        />
      </div>

      <JournalModalPanel isShow={modalConclusion} onClick={() => setModalConclusion(false)}>
        {conclusionTips}
      </JournalModalPanel>

      <ModalWrap isShow={isReportPickerOpen} className="!items-end !p-0 md:hidden">
        <button
          type="button"
          tabIndex={-1}
          aria-label="Закрыть выбор раздела"
          onClick={closeReportPicker}
          className="absolute inset-0 cursor-default focus-visible:outline-none"
        />
        <div
          ref={reportPickerDialogRef}
          id="journal-report-picker"
          role="dialog"
          aria-modal="true"
          aria-labelledby="journal-report-picker-title"
          className="relative z-10 max-h-[85dvh] w-full overflow-y-auto overscroll-contain rounded-t-3xl bg-white px-4 pb-[calc(20px+env(safe-area-inset-bottom))] pt-3 shadow-2xl"
        >
          <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-gray-300" aria-hidden />
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 id="journal-report-picker-title" className="text-pretty font-comfortaa text-lg font-bold text-blackBase">
              Выберите раздел
            </h2>
            <button
              type="button"
              aria-label="Закрыть выбор раздела"
              onClick={closeReportPicker}
              className="flex min-h-11 min-w-11 touch-manipulation items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-redBase"
            >
              <X size={22} aria-hidden />
            </button>
          </div>
          <div className="space-y-1">
            {[...reportTypes, 'add' as const].map((type) => {
              const isActive = activeReport === type;
              const label = type === 'add' ? 'Добавить отчёт' : type;
              return (
                <button
                  key={type}
                  type="button"
                  aria-current={isActive ? 'true' : undefined}
                  onClick={() => selectReport(type)}
                  className={`flex min-h-12 w-full touch-manipulation items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-redBase ${
                    isActive ? 'bg-red-50 text-redBase' : 'text-gray-800 hover:bg-gray-100'
                  }`}
                >
                  {type === 'add' ? <Plus size={19} aria-hidden /> : <ClipboardList size={19} aria-hidden />}
                  <span className="min-w-0 flex-1 break-words">{label}</span>
                  {isActive && <Check size={20} className="shrink-0" aria-hidden />}
                </button>
              );
            })}
          </div>
        </div>
      </ModalWrap>
    </div>
  );
}
